export function vibrate(pattern: number | number[]) {
  try {
    navigator.vibrate?.(pattern);
  } catch {
    // unsupported (iOS Safari) — silently ignore
  }
}

/** Keeps the screen on while running; browsers suspend GPS when the screen locks. */
export class ScreenWakeLock {
  private lock: { release(): Promise<void> } | null = null;
  private wanted = false;
  private readonly onVisible = () => {
    if (this.wanted && document.visibilityState === 'visible') void this.acquire();
  };

  async enable() {
    this.wanted = true;
    document.addEventListener('visibilitychange', this.onVisible);
    await this.acquire();
  }

  async disable() {
    this.wanted = false;
    document.removeEventListener('visibilitychange', this.onVisible);
    try {
      await this.lock?.release();
    } catch {
      /* already released */
    }
    this.lock = null;
  }

  get supported() {
    return 'wakeLock' in navigator;
  }

  private async acquire() {
    try {
      const wl = (navigator as Navigator & { wakeLock?: { request(t: 'screen'): Promise<{ release(): Promise<void> }> } }).wakeLock;
      this.lock = (await wl?.request('screen')) ?? null;
    } catch {
      this.lock = null;
    }
  }
}

export function isIos(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

export function isStandalone(): boolean {
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}
